import { useMemo } from 'react';
import { ProjectStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { MonitorPrompt } from '../../../../shared/models/monitor/MonitorPrompt';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import { useProjectData } from './UseProjectData';
import { KeywordResponse } from '../../../../shared/models/monitor/KeywordResponse';
import { findTagIdInKeyword } from '../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../shared/helpers/tags/TagsHelper';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import { buildAllowedChatTypes, expandSelectedChatTypes } from '../../../../shared/helpers/MonitorTypeHelpers';
import {
  useEffectiveAddons,
  useEffectiveFeatures,
} from '../../../../shared/contexts/effective-features/EffectiveFeaturesContext';

export const usePromptsData = (
  isKeywordDailyStatsForProjectLoading: boolean,
  isEventsLoading: boolean,
  keywordsDailyStatsForProject: ProjectStatsResponse,
  keywordsMap: Map<string, string>,
  monitorPrompts: MonitorPrompt[] | null,
  selectedChatType: ChatType | null,
  accountResponse: AccountResponse | null,
  commonKeywords: KeywordResponse[],
  tags: TagsResponse[],
  selectedChatTypes?: ChatType[]
) => {
  const { prepareAndSetPromptsData } = useProjectData();
  // Owner's models/addons when a superadmin views a client's project; own config elsewhere.
  const effectiveFeatures = useEffectiveFeatures();
  const effectiveAddons = useEffectiveAddons();
  return useMemo(() => {
    if (
      isKeywordDailyStatsForProjectLoading ||
      isEventsLoading ||
      !keywordsDailyStatsForProject ||
      keywordsMap.size === 0 ||
      !monitorPrompts ||
      !commonKeywords ||
      !tags
    ) {
      return null;
    }

    // NEW_STATS accounts: the "ALL" average spans every model we have data for; only the user's
    // explicit filter narrows it. Without the addon, keep the legacy account-feature gating.
    const hasNewStats = effectiveAddons.includes('NEW_STATS');
    let allowedChatTypes: Set<string> | undefined;

    if (hasNewStats) {
      allowedChatTypes =
        selectedChatTypes && selectedChatTypes.length > 0
          ? expandSelectedChatTypes(selectedChatTypes)
          : undefined;
    } else if (accountResponse?.features) {
      const accountAllowed = buildAllowedChatTypes(effectiveFeatures);

      if (selectedChatTypes && selectedChatTypes.length > 0) {
        const selectedExpanded = expandSelectedChatTypes(selectedChatTypes);
        allowedChatTypes = new Set(Array.from(accountAllowed).filter((ct) => selectedExpanded.has(ct)));
      } else {
        allowedChatTypes = accountAllowed;
      }
    }

    const data = prepareAndSetPromptsData(
      monitorPrompts,
      keywordsDailyStatsForProject.monitorPromptsKeywordsStatsResponse,
      selectedChatType || 'GPT',
      allowedChatTypes
    );

    // Per-prompt ChatGPT-Ads / Shopping-Cards percentages arrive pre-computed from the backend
    // (keyed by monitorId) — no separate features request or client-side merge needed.
    const featuresByMonitor = new Map(
      (keywordsDailyStatsForProject.promptFeatures ?? []).map((f) => [f.monitorId, f])
    );

    return data.map((prompt) => {
      const tagId = findTagIdInKeyword(prompt.keyword, commonKeywords);
      const keyword = getTagLabel(tags, tagId, prompt.keyword);
      const features = prompt.id != null ? featuresByMonitor.get(prompt.id) : undefined;
      return {
        ...prompt,
        keyword: keywordsMap.get(keyword) || keyword,
        isRequiredAttention: !!(accountResponse?.isSuperAdmin && prompt.isRequiredAttention),
        chatgptAdsPercent: features?.chatgptAdsPercent ?? null,
        shoppingCardsPercent: features?.shoppingCardsPercent ?? null,
      };
    });
  }, [
    keywordsDailyStatsForProject,
    isEventsLoading,
    monitorPrompts,
    selectedChatType,
    selectedChatTypes,
    keywordsMap,
    accountResponse,
    effectiveFeatures,
    effectiveAddons,
    commonKeywords,
    tags,
  ]);
};
